import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Space, Button, Flex, Dropdown, Alert } from "antd";
import { SettingOutlined, BellOutlined } from "@ant-design/icons";
import MenuBar from "./MenuBar";
import AppAvatar from "../components/AppAvatar";
import "./AppHeader.css";
import useAuth from "../hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrganisationContentAsync, fetchOrganisationItemAsync, selectContent } from "../features/organisations/organisationsSlice";
import { fetchNotificationsAsync } from "../features/notifications/notificationsSlice";
import StorageService from "../util/storage";
import { fetchUser } from "../features/user/userAPI";
import packageJson from '../../package.json';

export default function AppHeader() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { auth } = useAuth();
    const [alert] = useState(true);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [isOutdated, setIsOutdated] = useState(false);

    const organisationContent = useSelector(selectContent);
    const organisationStatus = useSelector((state) => state.organisation.status);
    const organisationError = useSelector((state) => state.organisation.error);
    const data = useSelector((state) => state.notifications.notifications);
    const status = useSelector((state) => state.notifications.status);
    const error = useSelector((state) => state.notifications.error);
    const org = useSelector((state) => state.organisation.organisation);
    const manualApprovalEnabled = org?.attributes?.LMS.enable_manual_approval;

    useEffect(() => {
        if (organisationStatus === "idle" || (organisationStatus === "fulfilled" && !organisationContent)) {
            dispatch(fetchOrganisationItemAsync());
            dispatch(fetchOrganisationContentAsync());
        }
        if (auth.isLoggedIn()) {
            if (status === "idle" || (status === "fulfilled" && !data)) {
                dispatch(fetchNotificationsAsync());
            }
        }
    }, [auth, organisationContent, dispatch, status, organisationStatus, data]);

    useEffect(() => {
        if (!data || data.length === 0) setUnreadNotifications(0);
        const lastRead = new Date(StorageService.getItem("lastRead"));
        setUnreadNotifications(data.filter((item) => new Date(item.attributes.createdAt) > lastRead).length);
    }, [data]);

    useEffect(() => {
        const currentVersion = packageJson.version;
        const savedVersion = localStorage.getItem('appVersion');
        if (savedVersion !== currentVersion) {
            setIsOutdated(true);
        }
    }, []);

    const handleRefresh = () => {
        const currentVersion = packageJson.version;
        localStorage.setItem('appVersion', currentVersion);
        window.location.reload(true);
    };

    const getItems = () => {
        if (!data || data.length === 0) return [];
        return data?.map((item) => {
            return {
                label: item.attributes.message,
                key: item.id,
            };
        });
    };

    const onchange = () => {
        StorageService.setItem("lastRead", new Date().toISOString());
        setUnreadNotifications(0);
    };

    if (status === "pending" || organisationStatus === "pending" || !organisationContent)
        return <div className="AlertInfo Loading">Loading...</div>;
    if (error || organisationError) return <p>Error :(</p>;

    const user = auth.getUser();

    const refetchUser = async () => {
        const newUserObject = await fetchUser(user.id);
        StorageService.setItem("user", {
            ...user,
            confirmed: newUserObject.attributes.confirmed,
            isUserApproved: newUserObject.attributes.isUserApproved,
        });
        window.location.reload();
    };

    return (
        <div className="AppHeader">
            {isOutdated && (
                <Alert
                    message="A new version of the app is available."
                    type="warning"
                    className="AppAlert Warning"
                    showIcon={false}
                    banner
                    action={
                        <Button
                            size="small"
                            danger
                            onClick={() => {
                                handleRefresh();
                            }}
                        >
                            Update
                        </Button>
                    }
                />
            )}
            {auth.isLoggedIn() && !user.confirmed && (
                <Alert
                    message="Please confirm account to remove restriction"
                    type="error"
                    className="AppAlert Small"
                    showIcon={false}
                    banner
                    action={
                        <Button
                            size="small"
                            danger
                            onClick={() => {
                                refetchUser();
                            }}
                        >
                            Refresh
                        </Button>
                    }
                />
            )}
            {auth.isLoggedIn() && user.confirmed && manualApprovalEnabled && !user.isUserApproved && (
                <Alert
                    message="Please wait for an admin to approve your account, you can contact them for more information"
                    type="error"
                    className="AppAlert Small"
                    showIcon={false}
                    banner
                    action={
                        <Button
                            size="small"
                            danger
                            onClick={() => {
                                refetchUser();
                            }}
                        >
                            Refresh
                        </Button>
                    }
                />
            )}
            <div className="AppContainer">
                <Flex justify="space-between" align="center">
                    <Link to="/">
                        <img src={organisationContent.attributes.logo.data.attributes.url} height={70} alt="" />
                    </Link>
                    <MenuBar />
                    {auth.isLoggedIn() ? (
                        <Space size="small">
                            <Button href="/settings" shape="circle" size="large" icon={<SettingOutlined />} />
                            <Dropdown
                                menu={{ items: getItems() }}
                                trigger={["click"]}
                                overlayClassName="AppNotifications"
                                onOpenChange={onchange}
                            >
                                <Badge dot={unreadNotifications > 0 ? alert : null} showZero={false} count={unreadNotifications}>
                                    <Button shape="circle" size="large" icon={<BellOutlined />} />
                                </Badge>
                            </Dropdown>
                            <AppAvatar />
                        </Space>
                    ) : (
                        <Button onClick={() => navigate("/login")}>Log In</Button>
                    )}
                </Flex>
            </div>
        </div>
    );
}
